import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTheme } from '@/store/useTheme'
import { PALETTES } from '@/store/palettes'
import { returnAfterPractice } from '@/utils/practiceReturn'

// ── Geometry ──────────────────────────────────────────────────────────────────
const SQ       = 220                         // square side (px)
const R        = 20                          // corner radius
const STRAIGHT = SQ - 2 * R                 // straight edge length
const ARC_Q    = (Math.PI / 2) * R          // quarter-circle arc length
const PERIM    = 4 * STRAIGHT + 4 * ARC_Q   // full perimeter

// CW SVG path: starts top-left → right → down → left → up
const CW_PATH = [
  `M ${R},0`,
  `L ${SQ - R},0 A ${R},${R} 0 0 1 ${SQ},${R}`,
  `L ${SQ},${SQ - R} A ${R},${R} 0 0 1 ${SQ - R},${SQ}`,
  `L ${R},${SQ} A ${R},${R} 0 0 1 0,${SQ - R}`,
  `L 0,${R} A ${R},${R} 0 0 1 ${R},0`,
].join(' ')

// Point at distance `dist` along the CW perimeter
function ptCW(dist: number): { x: number; y: number } {
  let d = ((dist % PERIM) + PERIM) % PERIM
  const seg = (len: number, fn: (t: number) => { x: number; y: number }) => {
    if (d <= len) return fn(d / len)
    d -= len
    return null
  }
  return (
    seg(STRAIGHT, t => ({ x: R + t * STRAIGHT,               y: 0 }))                                    ??
    seg(ARC_Q,    t => ({ x: (SQ-R) + R*Math.sin(t*Math.PI/2), y: R - R*Math.cos(t*Math.PI/2) }))         ??
    seg(STRAIGHT, t => ({ x: SQ,                              y: R + t * STRAIGHT }))                     ??
    seg(ARC_Q,    t => ({ x: (SQ-R) + R*Math.cos(t*Math.PI/2), y: (SQ-R) + R*Math.sin(t*Math.PI/2) }))   ??
    seg(STRAIGHT, t => ({ x: SQ - R - t * STRAIGHT,           y: SQ }))                                   ??
    seg(ARC_Q,    t => ({ x: R - R*Math.sin(t*Math.PI/2),     y: (SQ-R) + R*Math.cos(t*Math.PI/2) }))    ??
    seg(STRAIGHT, t => ({ x: 0,                               y: SQ - R - t * STRAIGHT }))                ??
    seg(ARC_Q,    t => ({ x: R - R*Math.cos(t*Math.PI/2),     y: R - R*Math.sin(t*Math.PI/2) }))          ??
    { x: R, y: 0 }
  )
}

const hexToRgb = (hex: string): [number, number, number] => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16),
]

// ── Phase helpers ─────────────────────────────────────────────────────────────
type BoxPhase = 'inhale' | 'hold-in' | 'exhale' | 'hold-out'
const PHASES: readonly BoxPhase[] = ['inhale', 'hold-in', 'exhale', 'hold-out']
const PHASE_LABEL: Record<BoxPhase, string> = {
  'inhale': 'Inhale', 'hold-in': 'Hold', 'exhale': 'Exhale', 'hold-out': 'Hold',
}

// Fill opacity ramps: faint at rest, richest on hold-in, fades on exhale
function getFillOpacity(phase: BoxPhase, prog: number): number {
  if (phase === 'inhale')   return 0.05 + prog * 0.45
  if (phase === 'hold-in')  return 0.50
  if (phase === 'exhale')   return 0.50 - prog * 0.45
  return 0.05 // hold-out
}

// ── Animation ref ─────────────────────────────────────────────────────────────
interface RunRef {
  phaseIdx: number
  startTime: number | null
  spc:   number   // seconds per count
  beats: number
}

// ── Persistence ───────────────────────────────────────────────────────────────
const BOX_KEY  = 'fitutogether-box-pacer'
const paceToSPC = (p: number) => 2.5 - (p - 1) * (1.9 / 9)

function readPref<T>(key: string, fallback: T): T {
  try {
    const stored = JSON.parse(localStorage.getItem(BOX_KEY) ?? '{}')
    return (stored[key] as T) ?? fallback
  } catch { return fallback }
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function BoxBreathingPage() {
  const navigate       = useNavigate()
  const [searchParams] = useSearchParams()
  const returnTo       = searchParams.get('return') ?? '/before-we-begin'
  const { palette }    = useTheme()

  // Persisted prefs
  const [beats,     setBeats]     = useState<number>      (() => readPref('beats',     4))
  const [pace,      setPace]      = useState<number>      (() => readPref('pace',      5))
  const [paletteId, setPaletteId] = useState<string|null> (() => readPref('paletteId', null))
  const [direction, setDirection] = useState<'cw'|'ccw'>  (() => readPref('direction', 'cw'))

  // Animation state
  const [phaseIdx,  setPhaseIdx]  = useState(0)
  const [progress,  setProgress]  = useState(0)
  const [count,     setCount]     = useState(0)
  const [isRunning, setIsRunning] = useState(false)

  // Timer
  const [timerMin,       setTimerMin]       = useState(5)
  const [timerActive,    setTimerActive]    = useState(false)
  const [timerRemaining, setTimerRemaining] = useState<number | null>(null)
  const [showTimerPick,  setShowTimerPick]  = useState(false)
  const [customMin,      setCustomMin]      = useState('')

  const frameRef = useRef<number | null>(null)
  const runRef   = useRef<RunRef>({ phaseIdx: 0, startTime: null, spc: paceToSPC(5), beats: 4 })

  // Keep ref in sync
  useEffect(() => { runRef.current.spc   = paceToSPC(pace) }, [pace])
  useEffect(() => { runRef.current.beats = beats },           [beats])

  // Persist prefs
  useEffect(() => {
    try { localStorage.setItem(BOX_KEY, JSON.stringify({ beats, pace, paletteId, direction })) }
    catch { /* quota — ignore */ }
  }, [beats, pace, paletteId, direction])

  // rAF animation loop
  useEffect(() => {
    if (!isRunning) {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
      setProgress(0); setPhaseIdx(0); setCount(0)
      runRef.current.startTime = null
      runRef.current.phaseIdx  = 0
      return
    }
    runRef.current.phaseIdx  = 0
    runRef.current.startTime = null

    const animate = (ts: number) => {
      const r        = runRef.current
      if (!r.startTime) r.startTime = ts
      const duration = r.beats * r.spc * 1000
      const elapsed  = ts - r.startTime
      const prog     = Math.min(elapsed / duration, 1)
      const c        = Math.min(Math.floor(prog * r.beats) + 1, r.beats)

      setPhaseIdx(r.phaseIdx)
      setProgress(prog)
      setCount(c)

      if (prog >= 1) {
        const excess    = elapsed - duration
        r.phaseIdx      = (r.phaseIdx + 1) % 4
        r.startTime     = ts - excess  // carry-over prevents timing snap
      }
      frameRef.current = requestAnimationFrame(animate)
    }

    frameRef.current = requestAnimationFrame(animate)
    return () => { if (frameRef.current !== null) cancelAnimationFrame(frameRef.current) }
  }, [isRunning])

  // Timer countdown — only ticks while breathing is running
  useEffect(() => {
    if (!timerActive || !isRunning || timerRemaining === null) return
    const id = setInterval(() => {
      setTimerRemaining(s => {
        if (s === null || s <= 1) {
          setTimerActive(false)
          setIsRunning(false)
          return null
        }
        return s - 1
      })
    }, 1000)
    return () => clearInterval(id)
  }, [timerActive, isRunning, timerRemaining])

  // ── Derived values ───────────────────────────────────────────────────────────
  const phase       = PHASES[phaseIdx]
  const spc         = paceToSPC(pace)
  const selPal      = paletteId ? PALETTES.find(p => p.id === paletteId) : null
  const ringColor   = selPal?.ring   ?? palette.ring
  const [fr, fg, fb] = hexToRgb(ringColor)

  const totalProg  = (phaseIdx + progress) / 4
  const tracedLen  = isRunning ? totalProg * PERIM : 0
  const dashOffset = PERIM - tracedLen

  // Dot position — mirror x-axis for CCW
  const rawDot = ptCW(tracedLen)
  const dotX   = direction === 'ccw' ? SQ - rawDot.x : rawDot.x
  const dotY   = rawDot.y

  const fOpacity   = isRunning ? getFillOpacity(phase, progress) : 0.04
  const beatSecs   = (beats * spc).toFixed(1)

  // Timer display
  const timerFrac = timerRemaining !== null ? timerRemaining / (timerMin * 60) : 1
  const timerMM   = timerRemaining !== null ? Math.floor(timerRemaining / 60).toString().padStart(2, '0') : '--'
  const timerSS   = timerRemaining !== null ? (timerRemaining % 60).toString().padStart(2, '0') : '--'
  const CLOCK_R   = 9   // radius of sweeping arc
  const CLOCK_C   = 2 * Math.PI * CLOCK_R

  // SVG transform for CCW mirror
  const ccwTransform = `translate(${SQ},0) scale(-1,1)`

  const pill: React.CSSProperties = {
    background:   'rgba(0,0,0,0.18)',
    borderRadius: 20,
    padding:      '4px 14px',
    color:        'rgba(255,255,255,0.85)',
  }

  return (
    <div style={{
      width: '100vw', height: '100dvh',
      overflow: 'hidden', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      background: palette.bg,
    }}>
      <div style={{
        width: '100%', maxWidth: 390,
        height: '100%', maxHeight: 667,
        margin: '0 auto',
        fontFamily: "'Lexend', sans-serif",
        position: 'relative', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', gap: 12,
        padding: '20px 24px 18px',
        background: palette.bg,
        borderRadius: 24,
        border: `1.5px solid ${palette.ring}40`,
      }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Lexend:wght@200;300;400&display=swap');
          * { box-sizing: border-box; margin: 0; padding: 0; }
          input[type=range] {
            -webkit-appearance: none; width: 100%; height: 2px;
            background: ${palette.ring}25; border-radius: 1px; outline: none; cursor: pointer;
          }
          input[type=range]::-webkit-slider-thumb {
            -webkit-appearance: none; width: 14px; height: 14px; border-radius: 50%;
            background: ${ringColor}; cursor: pointer; transition: transform 0.15s;
          }
          input[type=range]:hover::-webkit-slider-thumb { transform: scale(1.2); }
        `}</style>

        {/* ── 1. Top row: phase label (centre) · timer (right) ── */}
        <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
          {/* Left spacer mirrors the timer button width so the label sits dead-centre */}
          <div style={{ width: 30, flexShrink: 0 }} />

          <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <div style={{ fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase', fontWeight: 300, ...pill }}>
              {isRunning ? PHASE_LABEL[phase] : 'box breathing'}
            </div>
          </div>

          <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <button
              onClick={() => {
                if (timerActive) { setTimerActive(false); setTimerRemaining(null); setIsRunning(false) }
                else setShowTimerPick(p => !p)
              }}
              aria-label={timerActive ? 'Stop timer' : 'Set timer'}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {timerActive && (
                <span style={{ fontSize: 11, fontFamily: "'Lexend',sans-serif", color: palette.text, opacity: 0.72, letterSpacing: '0.06em' }}>
                  {timerMM}:{timerSS}
                </span>
              )}
              <svg width={22} height={22} viewBox="0 0 22 22" fill="none">
                <circle cx={11} cy={11} r={CLOCK_R} stroke={palette.ring} strokeOpacity={0.35} strokeWidth={1.5} />
                <path d="M11 7v4l2.5 2.5" stroke={palette.ring} strokeOpacity={0.45} strokeWidth={1.5} strokeLinecap="round" />
                {timerActive && (
                  <circle cx={11} cy={11} r={CLOCK_R}
                    stroke={ringColor} strokeWidth={2}
                    strokeDasharray={`${CLOCK_C}`}
                    strokeDashoffset={`${CLOCK_C * (1 - timerFrac)}`}
                    strokeLinecap="round"
                    style={{ transform: 'rotate(-90deg)', transformOrigin: '11px 11px', transition: 'stroke-dashoffset 1s linear' }}
                  />
                )}
              </svg>
            </button>

            {showTimerPick && (
              <div style={{
                position: 'absolute', top: 32, right: 0, zIndex: 20,
                background: palette.bg, border: `1px solid ${palette.ring}30`,
                borderRadius: 12, padding: '8px 10px',
                display: 'flex', flexWrap: 'wrap', gap: 6, maxWidth: 240,
                boxShadow: '0 4px 20px rgba(0,0,0,0.10)',
              }}>
                {[1, 3, 5, 10, 15].map(m => (
                  <button key={m}
                    onClick={() => { setTimerMin(m); setTimerRemaining(m * 60); setTimerActive(true); setShowTimerPick(false); setIsRunning(true) }}
                    style={{
                      background: 'none', border: `1px solid ${palette.ring}40`,
                      borderRadius: 8, padding: '4px 8px', fontSize: 11, cursor: 'pointer',
                      fontFamily: "'Lexend',sans-serif", color: palette.text, letterSpacing: '0.04em',
                    }}
                  >{m}m</button>
                ))}
                {/* Custom minutes input */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input
                    type="number" min={1} max={180}
                    value={customMin}
                    onChange={e => setCustomMin(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        const m = parseInt(customMin, 10)
                        if (m >= 1) { setTimerMin(m); setTimerRemaining(m * 60); setTimerActive(true); setShowTimerPick(false); setIsRunning(true); setCustomMin('') }
                      }
                    }}
                    placeholder="m"
                    style={{
                      width: 36, border: `1px solid ${palette.ring}40`,
                      borderRadius: 6, padding: '3px 6px', fontSize: 11,
                      fontFamily: "'Lexend',sans-serif", color: palette.text,
                      background: 'transparent', outline: 'none', textAlign: 'center',
                    }}
                  />
                  <button
                    onClick={() => {
                      const m = parseInt(customMin, 10)
                      if (m >= 1) { setTimerMin(m); setTimerRemaining(m * 60); setTimerActive(true); setShowTimerPick(false); setIsRunning(true); setCustomMin('') }
                    }}
                    style={{
                      background: 'none', border: `1px solid ${palette.ring}40`,
                      borderRadius: 8, padding: '4px 8px', fontSize: 11, cursor: 'pointer',
                      fontFamily: "'Lexend',sans-serif", color: palette.text, letterSpacing: '0.04em',
                    }}
                  >set</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── 2. The box ── */}
        <div style={{ position: 'relative', width: SQ, height: SQ, flexShrink: 0 }}>
          <div style={{
            position: 'absolute', inset: 0, borderRadius: R,
            background: `rgba(${fr},${fg},${fb},${fOpacity})`,
            transition: 'background 0.06s linear',
          }} />
          <svg width={SQ} height={SQ} viewBox={`0 0 ${SQ} ${SQ}`}
            style={{ position: 'absolute', inset: 0, overflow: 'visible' }}>
            <path d={CW_PATH} fill="none" stroke={ringColor} strokeOpacity={0.12} strokeWidth={1.5} />
            {isRunning && (
              <path d={CW_PATH} fill="none"
                stroke={ringColor} strokeWidth={2.5}
                strokeDasharray={PERIM} strokeDashoffset={dashOffset}
                strokeLinecap="round"
                transform={direction === 'ccw' ? ccwTransform : undefined}
              />
            )}
            {isRunning && <circle cx={dotX} cy={dotY} r={5} fill={ringColor} />}
          </svg>
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 4,
            pointerEvents: 'none',
          }}>
            <span style={{
              fontSize: isRunning ? 52 : 26, fontWeight: 200,
              color: isRunning ? ringColor : `${palette.ring}60`,
              lineHeight: 1, letterSpacing: '-0.02em',
              textShadow: '0 1px 6px rgba(0,0,0,0.10)',
              transition: 'font-size 0.3s ease',
            }}>
              {isRunning ? count : '·'}
            </span>
            {isRunning && (
              <span style={{ fontSize: 9, color: `${ringColor}99`, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                of {beats}
              </span>
            )}
          </div>
        </div>

        {/* ── 3. Beats selector ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: `${palette.text}50` }}>beats</span>
          {[2, 3, 4, 5, 6].map(b => (
            <button key={b} onClick={() => setBeats(b)} style={{
              width: 28, height: 28, borderRadius: '50%', padding: 0,
              background: beats === b ? ringColor : 'transparent',
              border: `1.5px solid ${beats === b ? ringColor : `${palette.ring}35`}`,
              color: beats === b ? 'white' : `${palette.text}65`,
              fontSize: 11, fontFamily: "'Lexend',sans-serif",
              fontWeight: beats === b ? 400 : 300,
              cursor: 'pointer', transition: 'all 0.2s ease',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>{b}</button>
          ))}
        </div>

        {/* ── 4. Begin / Pause ── */}
        <button onClick={() => setIsRunning(r => !r)} style={{
          background: isRunning ? 'rgba(0,0,0,0.05)' : ringColor,
          color: isRunning ? `${palette.text}55` : 'white',
          border: isRunning ? `1px solid ${palette.ring}25` : 'none',
          borderRadius: 20, padding: '10px 44px',
          fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
          cursor: 'pointer', fontFamily: "'Lexend',sans-serif", fontWeight: 300,
          transition: 'all 0.3s ease',
        }}>
          {isRunning ? 'pause' : 'begin'}
        </button>

        {/* ── 5. Done ── */}
        <button onClick={() => navigate(returnAfterPractice(returnTo))} style={{
          background: 'none', border: 'none',
          color: `${palette.text}45`, fontSize: 11,
          letterSpacing: '0.18em', textTransform: 'uppercase',
          cursor: 'pointer', fontFamily: "'Lexend',sans-serif", fontWeight: 300,
          padding: '2px 12px', marginTop: -4,
        }}>
          done
        </button>

        {/* ── 6. Pace slider ── */}
        <div style={{
          width: 210, display: 'flex', flexDirection: 'column', gap: 8,
          background: 'rgba(0,0,0,0.05)', borderRadius: 14, padding: '8px 14px',
        }}>
          <div style={{ fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase', color: `${palette.text}50`, textAlign: 'center' }}>
            pace
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 9, color: `${palette.text}40`, flexShrink: 0 }}>slow</span>
            <input type="range" min={1} max={10} step={1} value={pace} onChange={e => setPace(Number(e.target.value))} />
            <span style={{ fontSize: 9, color: `${palette.text}40`, flexShrink: 0 }}>fast</span>
          </div>
        </div>

        {/* ── 7. Colour swatches ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {PALETTES.map(p => {
            const sel = paletteId === p.id
            return (
              <button key={p.id}
                onClick={() => setPaletteId(prev => prev === p.id ? null : p.id)}
                aria-label={p.label} aria-pressed={sel}
                style={{
                  width: 20, height: 20, borderRadius: '50%', padding: 0,
                  background: p.swatch,
                  border: `1.5px solid ${sel ? p.ring : 'rgba(0,0,0,0.12)'}`,
                  cursor: 'pointer', outline: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: sel ? `0 0 0 1.5px ${p.ring}` : 'none',
                  transition: 'all 0.2s ease',
                  fontSize: 9, color: p.text, fontFamily: "'Lexend',sans-serif",
                }}
              >{sel ? '✓' : ''}</button>
            )
          })}
        </div>

        {/* ── 8. Bottom row: cycle timing (left) · direction toggle (right) ── */}
        <div style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto' }}>
          <span style={{ fontSize: 9, color: `${palette.text}40`, letterSpacing: '0.06em', fontFamily: "'Lexend',sans-serif" }}>
            {beatSecs}s per phase
          </span>
          <button onClick={() => setDirection(d => d === 'cw' ? 'ccw' : 'cw')} style={{
            background: 'none', border: 'none', padding: 0,
            display: 'flex', alignItems: 'center', gap: 3,
            fontSize: 9, letterSpacing: '0.08em',
            color: `${palette.text}40`, fontFamily: "'Lexend',sans-serif", fontWeight: 300,
            cursor: 'pointer',
          }}>
            <span style={{ fontSize: 11 }}>{direction === 'cw' ? '↻' : '↺'}</span>
            {direction === 'cw' ? 'clockwise' : 'anti-clockwise'}
          </button>
        </div>

      </div>
    </div>
  )
}
